import { useState, useEffect } from 'react';
import { Building2, Search, Plus, Trash2, Save } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const HospitalRateMaster = () => {
   const [hospitals, setHospitals] = useState([]);
   const [selectedHospitalId, setSelectedHospitalId] = useState('');
   const [selectedHospital, setSelectedHospital] = useState(null);
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [searchQuery, setSearchQuery] = useState('');

   // Billing items state
   const [tariffItems, setTariffItems] = useState([]);
   const [platformFee, setPlatformFee] = useState('35');
   const [platformFeeType, setPlatformFeeType] = useState('percent');
   const [platformFeeEnabled, setPlatformFeeEnabled] = useState(true);
   const [gstPercent, setGstPercent] = useState('18');
   const [billingNotes, setBillingNotes] = useState('');

   useEffect(() => {
      fetchHospitals();
   }, []);

   const fetchHospitals = async () => {
      setLoading(true);
      try {
         const res = await api.get('/hospitals?limit=1000');
         const rawData = res.data?.data?.hospitals || res.data?.hospitals || res.data?.data || res.data;
         const validList = Array.isArray(rawData) ? rawData : (Array.isArray(rawData?.hospitals) ? rawData.hospitals : []);
         setHospitals(validList);
         if (validList.length > 0 && !selectedHospitalId) {
            loadHospitalData(validList[0]._id, validList);
         }
      } catch (err) {
         console.error("Failed to load hospitals:", err);
         toast.error("Failed to load hospitals list");
         setHospitals([]);
      } finally {
         setLoading(false);
      }
   };

   const loadHospitalData = (id, list = hospitals) => {
      setSelectedHospitalId(id);
      const target = list.find(h => h._id === id);
      setSelectedHospital(target || null);

      if (target && target.standardBillingTemplate && target.standardBillingTemplate.baseItems && target.standardBillingTemplate.baseItems.length > 0) {
         const t = target.standardBillingTemplate;
         const baseItems = (t.baseItems || []).map(i => ({
            name: i.name,
            rate: i.rate || i.amount || 0,
            unitType: i.unitType || (i.days > 1 ? 'Per Day' : i.hours > 1 ? 'Per Hour' : 'Fixed')
         }));
         setTariffItems(baseItems);
         setPlatformFee(t.platformFeePercent ? t.platformFeePercent.toString() : '35');
         setPlatformFeeType(t.platformFeeType || 'percent');
         setPlatformFeeEnabled(t.platformFeeEnabled !== false);
         setGstPercent(t.gstPercent ? t.gstPercent.toString() : '18');
         setBillingNotes(t.notes || '');
      } else {
         setTariffItems([]);
         setPlatformFee('35');
         setPlatformFeeType('percent');
         setPlatformFeeEnabled(true);
         setGstPercent('18');
         setBillingNotes('');
      }
   };

   const handleAddRow = () => {
      setTariffItems(prev => [
         ...prev,
         { name: '', rate: 0, unitType: 'Fixed' }
      ]);
   };

   const handleRemoveRow = (index) => {
      setTariffItems(prev => prev.filter((_, idx) => idx !== index));
   };

   const handleUpdateRow = (index, field, value) => {
      setTariffItems(prev => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item));
   };

   const handleSaveTariff = async () => {
      if (!selectedHospitalId) {
         return toast.error("Please select a hospital first");
      }

      setSaving(true);
      try {
         const baseItems = tariffItems.map(i => ({
            name: i.name || 'Service',
            rate: Number(i.rate) || 0,
            amount: Number(i.rate) || 0,
            unitType: i.unitType || 'Fixed'
         }));

         const templateData = {
            baseItems: baseItems,
            manualItems: baseItems.map(i => ({
               name: i.name,
               type: 'add',
               amount: i.rate
            })),
            platformFeePercent: Number(platformFee) || 35,
            platformFeeType: platformFeeType,
            platformFeeEnabled: platformFeeEnabled,
            gstPercent: Number(gstPercent) || 18,
            notes: billingNotes,
            updatedAt: new Date()
         };

         await api.put(`/hospitals/${selectedHospitalId}`, {
            standardBillingTemplate: templateData
         });

         setHospitals(prev => prev.map(h => h._id === selectedHospitalId ? { ...h, standardBillingTemplate: templateData } : h));
         toast.success(`Tariff Card & Standard Billing saved for ${selectedHospital?.hospitalName || 'Hospital'}!`);
      } catch (err) {
         console.error("Failed to save tariff card:", err);
         toast.error(err.response?.data?.message || "Failed to save hospital tariff card");
      } finally {
         setSaving(false);
      }
   };

   const filteredItems = tariffItems.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
   );

   return (
      <div className="p-4 sm:p-8 space-y-8 max-w-7xl mx-auto text-left">
         {/* Page Header */}
         <div className="bg-gradient-to-r from-[#143E78] via-[#0f2c59] to-indigo-950 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
               <div>
                  <span className="px-3 py-1 bg-amber-400/20 text-amber-300 border border-amber-400/40 rounded-full text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 mb-3">
                     <Building2 className="w-3.5 h-3.5" /> Hospital Billing Master & Tariff Card
                  </span>
                  <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight">Hospital Rate Master</h1>
                  <p className="text-blue-200 text-xs font-bold mt-1 max-w-2xl">
                     Configure standard service items, rates, and billing structures per hospital. CC agents can view these exact official rates during hospital pay calculation.
                  </p>
               </div>
               <div className="flex flex-wrap items-center gap-3">
                  <button
                     onClick={handleSaveTariff}
                     disabled={saving || !selectedHospitalId}
                     className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-xl shadow-amber-950/20 cursor-pointer disabled:opacity-50"
                  >
                     <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Hospital Tariff Card"}
                  </button>
               </div>
            </div>
         </div>

         {/* Hospital Selector Bar */}
         <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
               <div>
                  <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                     <Building2 className="w-4 h-4 text-blue-600" /> Select Partner Hospital
                  </label>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Select a hospital to edit its official rates & billing structure</p>
               </div>
               <div className="w-full sm:w-80">
                  <select
                     value={selectedHospitalId}
                     onChange={(e) => loadHospitalData(e.target.value)}
                     className="w-full p-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl text-xs font-bold text-slate-800 outline-none focus:border-blue-600 transition-all cursor-pointer"
                  >
                     {(Array.isArray(hospitals) ? hospitals : []).map(h => (
                        <option key={h._id} value={h._id}>
                           {h.hospitalName || h.name || 'Hospital'} ({h.address?.city || 'City N/A'})
                        </option>
                     ))}
                  </select>
               </div>
            </div>

            {selectedHospital && (
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                     <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Primary Contact</span>
                     <span className="text-xs font-black text-slate-800">{selectedHospital.contactPerson?.name || selectedHospital.entityContactName || 'N/A'} ({selectedHospital.contactPerson?.phone || 'No phone'})</span>
                  </div>
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                     <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">City / Location</span>
                     <span className="text-xs font-black text-slate-800">{selectedHospital.address?.city || 'N/A'}, {selectedHospital.address?.state || ''}</span>
                  </div>
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                     <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Configured Services</span>
                     <span className="text-xs font-black text-emerald-600">{tariffItems.length} Billing Line Items</span>
                  </div>
               </div>
            )}
         </div>

         {/* Search & Service List Table */}
         <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
               <div>
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Service Rates & Billing Items ({filteredItems.length})</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Itemized services and standard rates for {selectedHospital?.hospitalName || 'hospital'}</p>
               </div>
               <div className="relative w-full sm:w-72">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                     type="text"
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                     placeholder="Search service..."
                     className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-600"
                  />
               </div>
            </div>

            {/* Table of Services */}
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
               <table className="w-full text-left border-collapse">
                  <thead>
                     <tr className="bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest border-b border-slate-200">
                        <th className="p-4 w-12 text-center">#</th>
                        <th className="p-4">Service Description</th>
                        <th className="p-4 w-44 text-right">Standard Rate (₹)</th>
                        <th className="p-4 w-44">Unit Type</th>
                        <th className="p-4 w-16 text-center">Action</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                     {filteredItems.length === 0 ? (
                        <tr>
                           <td colSpan={5} className="p-8 text-center text-slate-400 font-bold uppercase text-xs">
                              No service rate items found. Click "+ Add Service Item Row" to create one.
                           </td>
                        </tr>
                     ) : (
                        filteredItems.map((item, idx) => (
                           <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="p-4 text-center font-bold text-slate-400 text-[10px]">{idx + 1}</td>
                              <td className="p-3">
                                 <input
                                    type="text"
                                    value={item.name}
                                    onChange={(e) => handleUpdateRow(idx, 'name', e.target.value)}
                                    placeholder="Enter service description (e.g. OT Charges)..."
                                    className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-blue-600"
                                 />
                              </td>
                              <td className="p-3">
                                 <input
                                    type="number"
                                    value={item.rate === 0 ? '' : item.rate}
                                    onChange={(e) => handleUpdateRow(idx, 'rate', Number(e.target.value) || 0)}
                                    placeholder="0"
                                    className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 text-right outline-none focus:border-blue-600"
                                 />
                              </td>
                              <td className="p-3">
                                 <select
                                    value={item.unitType || 'Fixed'}
                                    onChange={(e) => handleUpdateRow(idx, 'unitType', e.target.value)}
                                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-600"
                                 >
                                    <option value="Fixed">Fixed Amount</option>
                                    <option value="Per Day">Per Day</option>
                                    <option value="Per Hour">Per Hour</option>
                                    <option value="Per Item">Per Item</option>
                                 </select>
                              </td>
                              <td className="p-3 text-center">
                                 <button
                                    onClick={() => handleRemoveRow(idx)}
                                    className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all cursor-pointer"
                                    title="Delete Service Item"
                                 >
                                    <Trash2 className="w-4 h-4" />
                                 </button>
                              </td>
                           </tr>
                        ))
                     )}
                  </tbody>
               </table>
            </div>

            <button
               onClick={handleAddRow}
               className="w-full py-3 bg-slate-100 hover:bg-slate-200 border border-dashed border-slate-300 rounded-2xl text-xs font-black text-slate-700 uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer"
            >
               <Plus className="w-4 h-4 text-blue-600" /> Add Service Item Row
            </button>
         </div>
      </div>
   );
};

export default HospitalRateMaster;
